import { useEffect, useState } from 'react'

import styled, { css } from 'styled-components'

import {
  NarrowContent,
  WideContent,
} from '@/components/layout-components/CorePageViewEntity'
import { useFilters } from '@/context/filterContext'
import { useLocale } from '@/context/localeContext'
import { useQueryParams } from '@/context/queryParamsContext'
import useHighlight from '@/hooks/useHighlight'
import { getDataTags } from '@/utils/getDataTags'

const Wrapper = styled.div<{ hideLine: boolean; $fillSpace?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${({ $fillSpace }) => ($fillSpace ? '0.5rem' : '1.875rem')};
  justify-content: flex-start;
  white-space: nowrap;
  overflow: auto;
  overflow-x: visible;

  ${({ hideLine }) =>
    !hideLine &&
    css`
      &::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 3px;
        background-color: ${({ theme }) => theme.colour.secondary._100};
      }
    `}
`

const TabButton = styled.button<{
  active: boolean
  green?: boolean
  $fillSpace?: boolean
}>`
  ${({ theme }) => theme.font.label01}
  font-weight: 600;
  color: ${({ theme, active, green }) =>
    active
      ? green
        ? theme.colour.theme.primaryTextColour
        : theme.colour.secondary.base
      : theme.colour.secondary._400};
  background-color: transparent;
  padding: 12px 4px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: ${({ $fillSpace }) => ($fillSpace ? '1' : 'none')};
  justify-content: ${({ $fillSpace }) =>
    $fillSpace ? 'center' : 'flex-start'};
`

const Info = styled.span`
  font-weight: 400;
  display: inline-flex; // important for vertical alignment
`

const HighLight = styled.div<{ green?: boolean }>`
  position: absolute;
  left: 0;
  background-color: ${({ theme, green }) =>
    green ? theme.colour.theme.primaryTextColour : theme.colour.secondary.base};
  height: 3px;
  width: 100%;
  transition: 0.2s;
  margin-top: -3px;
`

type TabDisplayProps = {
  show: boolean
  style?: React.CSSProperties
  children: React.ReactNode
}

/**
 * Sometimes we need the form items to always be rendered so that they exist in the Form. Take the CorePageViewAndEditForm for example.
 * If they are not rendered and the title or description is updated, the onEdit function will be called without any of the editable
 * form fields and will likely fail calling the API.
 */
export const TabDisplay: React.FC<TabDisplayProps> = ({
  show,
  style,
  children,
}) => {
  return (
    <div style={{ ...style, display: show ? 'block' : 'none' }}>{children}</div>
  )
}

type TabsWrapperProps = {
  wide?: boolean
  mt?: string
  children: React.ReactNode
}

export const TabsWrapper: React.FC<TabsWrapperProps> = ({
  wide,
  mt = '2rem',
  children,
}) => {
  const Wrapper = wide ? WideContent : NarrowContent

  return (
    <Wrapper column style={{ marginTop: mt }}>
      {children}
    </Wrapper>
  )
}

export const TabContent = styled.div`
  margin-top: 1.5rem;
`

type Props<T extends Record<string, string>> = {
  tabs: T
  initialTab?: keyof T
  onTabChanged?: (tab: keyof T) => void
  filterKey?: string
  children?: (
    tab: keyof T,
    setActiveTab: (tab: keyof T) => void
  ) => React.ReactNode
  info?: Partial<Record<keyof T, string | React.ReactNode>>
  green?: boolean
  hideLine?: boolean
  queryKey?: string
  useQuery?: boolean
  fillSpace?: boolean
}

export const Tabs = <T extends Record<string, string>>({
  tabs,
  initialTab,
  onTabChanged,
  filterKey,
  info,
  green,
  queryKey = 'tab',
  hideLine = true,
  useQuery,
  fillSpace = false,
  children,
}: Props<T>) => {
  const { routeFilters, updateFilters } = useFilters()
  const { params, updateParams } = useQueryParams()
  const { t } = useLocale()

  // check both "filters" and "extra" due to inconsistencies on how this is set
  const routeFilterValue = routeFilters?.filters?.[filterKey] as string
  const routeExtraValue = routeFilters?.extra?.[filterKey] as string

  const queryParamValue = params[queryKey] as string

  const defaultValue =
    initialTab ??
    (useQuery ? queryParamValue : null) ??
    routeFilterValue ??
    routeExtraValue ??
    Object.keys(tabs)[0]

  const [activeTab, setActiveTab] = useState<keyof T>(defaultValue)
  const { highlightPos, recalculate } = useHighlight(activeTab as string)

  const onSetActiveTab = (newTab: keyof T) => {
    if (newTab === activeTab) return

    if (useQuery) {
      updateParams({ [queryKey]: newTab as string })
    }

    setActiveTab(newTab)
    onTabChanged?.(newTab)
  }

  useEffect(() => {
    if (activeTab) {
      const newTab = tabs[activeTab]
        ? activeTab
        : (Object.keys(tabs)[0] as keyof T)

      if (newTab === activeTab) return

      onTabChanged?.(newTab)

      // if the tabs change, make sure the active tab is still valid
      if (useQuery) {
        updateParams({ [queryKey]: newTab as string })
      }
      setActiveTab(newTab)
    }
  }, [activeTab, onTabChanged, tabs, updateParams, useQuery, queryKey])

  // if the query param changes, update the active tab
  useEffect(() => {
    if (!useQuery || !queryParamValue) return

    if (!Object.keys(tabs).includes(queryParamValue)) return

    if (queryParamValue !== activeTab) {
      setActiveTab(queryParamValue as keyof T)
    }
  }, [activeTab, queryParamValue, tabs, useQuery])

  // the info can often be set asynchronously, so we need to
  // recalculate the highlight position when we have it
  useEffect(() => {
    if (!!info && Object.keys(info).length) {
      recalculate()
    }
  }, [info, recalculate])

  const handleTabChanged = (value: keyof T) => {
    setActiveTab(value)
    if (useQuery) {
      updateParams({ [queryKey]: value as string })
    }
    onTabChanged?.(value)
    if (filterKey) {
      updateFilters({ extra: { ...routeFilters?.extra, [filterKey]: value } })
    }
  }

  return (
    <>
      <Wrapper hideLine={hideLine} $fillSpace={fillSpace}>
        {Object.keys(tabs).map((tab) => (
          <TabButton
            key={tab}
            active={tab === activeTab}
            green={green}
            $fillSpace={fillSpace}
            onClick={() => handleTabChanged(tab)}
            className={`highlight-${tab}`}
            type="button"
            {...getDataTags({ name: tab, type: 'tab' })}
          >
            {t(tabs[tab])}
            <Info>{info?.[tab]}</Info>
          </TabButton>
        ))}
        {highlightPos && (
          <HighLight
            green={green}
            style={{
              top: highlightPos.offsetY + highlightPos.height,
              left: highlightPos.offsetX,
              width: highlightPos.width,
            }}
          />
        )}
      </Wrapper>
      {children?.(activeTab, onSetActiveTab)}
    </>
  )
}

export type TabsProps = Props<Record<string, string>>

Tabs.displayName = 'Tabs'
